import ReactDOM from "react-dom/client";
import React from "react";
import whyDidYouRender from "@welldone-software/why-did-you-render";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import store from "@web-src/store";
import { AppVariantProvider } from "@jugl-web/utils/providers/AppVariantProvider";
import { CustomThemeProvider } from "@web-src/themes/CustomThemeProvider";
import { SingletonHooksContainer } from "react-singleton-hook";
import { RestApiProvider } from "@jugl-web/rest-api";
import { FilePreviewProvider } from "@jugl-web/domain-resources/files/providers/FilePreviewProvider";
import { BrowserRouter as Router } from "react-router-dom";
import App from "@web-src/app/app";
import { PhoenixSocketProvider } from "@web-src/modules/socket/providers/PhoenixSocket";
import { AuthTokenProvider } from "@jugl-web/domain-resources/auth/providers/AuthTokenProvider";
import { PreferencesProvider } from "@jugl-web/domain-resources/preferences";
import { DownloadManagerProvider } from "@jugl-web/domain-resources/files/providers/DownloadManagerProvider";
import { EnhancedIntlProvider } from "@jugl-web/utils/i18n/EnhancedIntlProvider";
import AgoraRTC from "agora-rtc-react";
import {
  CallsProvider,
  CallsClient,
} from "@web-src/modules/conference/pages/ConferencePage/providers";
import { UIComponentsProvider } from "@jugl-web/domain-resources/ui-components";
import { UserProfileProvider } from "@jugl-web/domain-resources/users/providers/UserProfileProvider";
import { ChatBotProvider } from "@jugl-web/domain-resources/chat-bot";
import { PostHogProvider } from "@posthog/react";
import { environment } from "./environments/environment";
import {
  tasksApi,
  usersApi,
  workspacesApi,
  contactsApi,
  ticketsApi,
  customerApi as customersApi,
  customerFormFieldApi as customersFormFieldApi,
  tasksCommentsApi,
  dashboardApi,
  driveApi,
  tasksTemplatesApi,
  moduleNotificationsApi,
  authApi,
  entitiesApi,
  entitySpacesApi,
  linksApi,
  ordersApi,
  inventoryApi,
  integrationsApi,
  chatsApi,
  invoicesApi,
  aiAssistantApi,
  subscriptionV2Api,
  aiWorkflowsApi,
  shopifyApi,
} from "./modules/api/createApi";
import { getJwtToken } from "./modules/auth/utils/tokenStorage";
import { InAppNotificationSnackbar } from "./modules/notifications/components/InAppNotificationSnackbar";
import { NotificationsProvider } from "./modules/notifications/providers/NotificationsProvider";
import { TimeZoneProvider } from "./modules/settings/providers/TimeZoneProvider";
import { EntityProvider } from "./modules/entities/providers/EntityProvider";
import { UserAuthProvider } from "./modules/auth/providers/UserAuthProvider/UserAuthProvider";
import { AnonymousSessionProvider } from "./modules/auth/providers/AnonymousSessionProvider";
import { EntitySubscriptionProvider } from "./modules/entities/providers/EntitySubscriptionProvider";
import { SubscriptionV2Provider } from "./modules/cx/providers/SubscriptionV2Provider";
import { OnboardingFlowsProvider } from "./modules/cx/providers/OnboardingFlowsProvider";
import "./polyfills/mp3RecorderPolyfill";
import { SentryUserSetter } from "./modules/common/utils/sentrySetup";
import {
  posthogOptions,
  PostHogUserSetter,
} from "./modules/common/utils/posthogSetup";
import { OnboardingProvider } from "./modules/preferences/providers";
import { InAppNotification } from "./modules/notifications/providers/NotificationsProvider/NotificationsContext";
import { MediaPlayerProvider } from "./modules/media/providers/MediaPlayerProvider";
import { AppApiResultHandlingProvider } from "./modules/common/providers/AppApiResultHandlingProvider";
import { GlobalComponentsProvider } from "./modules/common/providers/GlobalComponentsProvider/GlobalComponentsProvider";
import { AppVersionSetter } from "./modules/common/components/AppVersionSetter";
import { captureGmailOAuthResult } from "./modules/client-support/utils/captureGmailOAuthResult";
import "./index.css";
import "./styles.css";

AgoraRTC.setLogLevel(4);

// NOTE: cleanup tus related stuff from user's localStorage
// TODO: to be removed in next release
(() => {
  const cleanupDoneKey = "jugl::tusCleanupDone";
  const cleanupDone = localStorage.getItem(cleanupDoneKey);
  if (cleanupDone) {
    return;
  }
  localStorage.setItem(cleanupDoneKey, "1");
  Object.keys(localStorage).forEach((key) => {
    if (!key.startsWith("tus::")) {
      return;
    }
    localStorage.removeItem(key);
  });
})();
//

if (process.env.NODE_ENV === "development" && environment.isWDIREnabled) {
  whyDidYouRender(React, {
    trackAllPureComponents: true,
  });
}

const renderApp = () => {
  const $rootElement = document.getElementById("root");
  if (!$rootElement) {
    return;
  }
  if (environment.isDemo) {
    localStorage.clear();
    sessionStorage.clear();
  }
  const root = ReactDOM.createRoot($rootElement);
  root.render(
    <AppVariantProvider
      variant="web"
      isDemo={environment.isDemo}
      defaultVersion="cx"
    >
      <EnhancedIntlProvider saveLangToLocalStorage>
        <UIComponentsProvider>
          <PreferencesProvider>
            <Provider store={store}>
              <RestApiProvider
                apiBaseUrl={environment.apiUrl}
                apis={{
                  tasksApi,
                  tasksCommentsApi,
                  tasksTemplatesApi,
                  usersApi,
                  workspacesApi,
                  customersApi,
                  contactsApi,
                  ticketsApi,
                  customersFormFieldApi,
                  dashboardApi,
                  driveApi,
                  moduleNotificationsApi,
                  authApi,
                  entitiesApi,
                  entitySpacesApi,
                  linksApi,
                  ordersApi,
                  inventoryApi,
                  integrationsApi,
                  chatsApi,
                  invoicesApi,
                  aiAssistantApi,
                  subscriptionV2Api,
                  aiWorkflowsApi,
                  shopifyApi,
                }}
              >
                <PostHogProvider
                  apiKey={environment.posthogApiKey}
                  options={posthogOptions}
                >
                  <Router>
                    <AnonymousSessionProvider>
                      <UserProfileProvider>
                        <EntityProvider>
                          <CustomThemeProvider>
                            <SnackbarProvider
                              domRoot={document.body}
                              maxSnack={3}
                              Components={{
                                inAppNotification: InAppNotificationSnackbar,
                              }}
                              classes={{ containerRoot: "!z-[2000]" }}
                            >
                              <AppApiResultHandlingProvider>
                                <NotificationsProvider>
                                  <TimeZoneProvider>
                                    <MediaPlayerProvider>
                                      <FilePreviewProvider>
                                        <AuthTokenProvider
                                          getter={() =>
                                            getJwtToken()?.accessToken || null
                                          }
                                        >
                                          <DownloadManagerProvider>
                                            <PhoenixSocketProvider>
                                              <CallsClient>
                                                <CallsProvider>
                                                  <UserAuthProvider>
                                                    <EntitySubscriptionProvider>
                                                      <GlobalComponentsProvider>
                                                        <ChatBotProvider
                                                          token={
                                                            environment.chatBotToken
                                                          }
                                                          chatBotUrl={
                                                            environment.chatBotUrl
                                                          }
                                                        >
                                                          <SubscriptionV2Provider>
                                                            <OnboardingFlowsProvider>
                                                              <OnboardingProvider>
                                                                <SentryUserSetter />
                                                                <PostHogUserSetter />
                                                                <AppVersionSetter />
                                                                <App />
                                                              </OnboardingProvider>
                                                            </OnboardingFlowsProvider>
                                                          </SubscriptionV2Provider>
                                                        </ChatBotProvider>
                                                      </GlobalComponentsProvider>
                                                    </EntitySubscriptionProvider>
                                                  </UserAuthProvider>
                                                  <SingletonHooksContainer />
                                                </CallsProvider>
                                              </CallsClient>
                                            </PhoenixSocketProvider>
                                          </DownloadManagerProvider>
                                        </AuthTokenProvider>
                                      </FilePreviewProvider>
                                    </MediaPlayerProvider>
                                  </TimeZoneProvider>
                                </NotificationsProvider>
                              </AppApiResultHandlingProvider>
                            </SnackbarProvider>
                          </CustomThemeProvider>
                        </EntityProvider>
                      </UserProfileProvider>
                    </AnonymousSessionProvider>
                  </Router>
                </PostHogProvider>
              </RestApiProvider>
            </Provider>
          </PreferencesProvider>
        </UIComponentsProvider>
      </EnhancedIntlProvider>
    </AppVariantProvider>
  );
};

// must run before <Router> mounts, while the OAuth callback query string is
// still intact on window.location.search
captureGmailOAuthResult();

renderApp();

// TODO: move out
declare module "notistack" {
  interface VariantOverrides {
    inAppNotification: {
      notification?: InAppNotification;
    };
  }
}
